import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Spinner,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { supabasePublic } from "../supabaseClient";
import { getBackendURL } from "../env";
import SchemaBadge from "../messaging/SchemaBadge";
import {
  addDraftSchema,
  removeDraftSchema,
  setNativeSchema,
} from "../../utils/userAccess";

// Signups dashboard → User Access: find ANY staff user by name (most recently
// active first, like the Messages people search) and edit the schemas they
// can open. The list lives on public.users.schema whichever schema the user
// works in; the first entry is the one they land in at login.
//
// It also holds each user's superadmin switch (public.users.superadmin, 2026-10-27): who may use
// the agents' superadmin tools (the CEO agent, the master agents' permissions). Only someone with
// superadmin access may change it (the search says so: can_set_superadmin), never their own, and
// not before the database has the column (superadmin_ready).
const SWITCH_WHY = {
  notReady: "Needs the database update in sql/migrations/2026-10-27-users-superadmin.sql.",
  own: "You can't change your own.",
  notAllowed: "Only a superadmin can change this.",
  about: "Can use the agents' superadmin tools: the CEO agent and the master agents' permissions.",
};

export async function searchUsersForAccess(query, { signal } = {}) {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session — sign in again");
  const res = await fetch(
    `${getBackendURL()}/api/admin/user-access/search/?q=${encodeURIComponent(query)}`,
    { headers: { Authorization: `Bearer ${token}` }, signal },
  );
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.error) {
    if (json.error === "caller_not_admin") throw new Error("Your role can't edit user access.");
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return {
    users: json.users || [],
    canSetSuperadmin: json.can_set_superadmin === true,
    superadminReady: json.superadmin_ready === true,
  };
}

export function formatLastActive(iso, now = Date.now()) {
  if (!iso) return "never active";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "never active";
  const mins = Math.max(0, Math.round((now - then) / 60000));
  if (mins < 1) return "active just now";
  if (mins < 60) return `active ${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `active ${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `active ${days}d ago`;
  return `active ${new Date(iso).toLocaleDateString()}`;
}

export default function UserAccessTab({ knownSchemas = [], renderRoleBadge, onSave, onSetSuperadmin }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState({ nativeInput: "", base: [], extraInput: "" });
  const [saving, setSaving] = useState(false);
  // From the latest search: whether the caller may change switches, and whether the column is there.
  const [switches, setSwitches] = useState({ canSet: false, ready: false });
  const [switching, setSwitching] = useState(false);

  // Debounced like the Messages picker; a newer keystroke aborts the older request.
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSearching(false);
      setSearchError(null);
      return undefined;
    }
    const controller = new AbortController();
    setSearching(true);
    const handle = setTimeout(async () => {
      try {
        const found = await searchUsersForAccess(query, { signal: controller.signal });
        setResults(found.users);
        setSwitches({ canSet: found.canSetSuperadmin, ready: found.superadminReady });
        setSearchError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        setResults([]);
        setSearchError(err.message);
      }
      setSearching(false);
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [query]);

  const pick = (u) => {
    setSelected(u);
    setDraft({ nativeInput: u.schemas[0] || "", base: [...u.schemas], extraInput: "" });
  };

  const schemaOptions = useMemo(() => {
    const seen = new Set(knownSchemas);
    (selected?.schemas || []).forEach((s) => seen.add(s));
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [knownSchemas, selected]);

  // The list to save: the native input moved to the front of `base`
  // (swapping with an extra, or pushing the old native into the extras).
  const draftSchemas = setNativeSchema(draft.base, draft.nativeInput);
  const setNative = (value) => setDraft((d) => ({ ...d, nativeInput: value }));
  const addExtra = (name) =>
    setDraft((d) => ({ ...d, extraInput: "", base: addDraftSchema(d.base, d.nativeInput, name) }));
  const removeExtra = (name) => setDraft((d) => ({ ...d, base: removeDraftSchema(d.base, name) }));

  const current = selected?.schemas || [];
  const changed = !!selected && draftSchemas.join(",") !== current.join(",");
  const canSave =
    !!selected && !!draft.nativeInput.trim() && draftSchemas.length > 0 && changed;

  // Why the switch can't be changed, or null when it can.
  let switchWhy = null;
  if (!switches.ready) switchWhy = SWITCH_WHY.notReady;
  else if (selected?.is_self) switchWhy = SWITCH_WHY.own;
  else if (!switches.canSet) switchWhy = SWITCH_WHY.notAllowed;

  // Turned at once; put back when the server refuses (onSetSuperadmin toasts why).
  const setSuperadmin = async (on) => {
    if (!selected || switchWhy || switching || !onSetSuperadmin) return;
    const before = selected;
    const apply = (value) => {
      const updated = { ...before, superadmin: value };
      setSelected(updated);
      setResults((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    };
    apply(on);
    setSwitching(true);
    try {
      const json = await onSetSuperadmin(before, on);
      apply(json && typeof json.superadmin === "boolean" ? json.superadmin : on);
    } catch {
      apply(before.superadmin === true);
    } finally {
      setSwitching(false);
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const json = await onSave(selected, draftSchemas);
      const updated = {
        ...selected,
        role: json.role,
        schemas: json.schemas,
        current_schema: json.schemas[0] || null,
      };
      setSelected(updated);
      setDraft({ nativeInput: json.schemas[0] || "", base: [...json.schemas], extraInput: "" });
      setResults((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch {
      // onSave already toasted the reason; keep the draft so it can be retried.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Flex gap={6} direction={{ base: "column", lg: "row" }} align="stretch">
      <Box flex="1" minW={0} maxW={{ lg: "420px" }}>
        <Input
          placeholder="Search people by name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          aria-label="Search users by name"
        />
        <Text fontSize="xs" color="gray.500" mt={1}>
          Name only, every clinic, most recently active first. Patients are managed in Patient Signups.
        </Text>
        <Box mt={3}>
          {searching && (
            <HStack fontSize="sm" color="gray.500">
              <Spinner size="xs" />
              <Text>Searching…</Text>
            </HStack>
          )}
          {!searching && searchError && (
            <Text fontSize="sm" color="red.500">{searchError}</Text>
          )}
          {!searching && !searchError && query.trim() && results.length === 0 && (
            <Text fontSize="sm" color="gray.500">No users match "{query.trim()}".</Text>
          )}
          {results.length > 0 && (
            <VStack align="stretch" spacing={0} borderWidth="1px" borderRadius="md" maxH="60vh" overflowY="auto">
              {results.map((u) => {
                const isSelected = selected?.id === u.id;
                return (
                  <Box
                    key={u.id}
                    as="button"
                    type="button"
                    textAlign="left"
                    p={2}
                    borderBottomWidth="1px"
                    bg={isSelected ? "blue.50" : undefined}
                    _hover={{ bg: isSelected ? "blue.50" : "gray.50" }}
                    onClick={() => pick(u)}
                  >
                    <HStack spacing={2} wrap="wrap">
                      <Text fontSize="sm" fontWeight="medium" overflowWrap="anywhere">
                        {u.name || "(no name)"}
                        {u.is_self ? " (you)" : ""}
                      </Text>
                      <SchemaBadge schema={u.current_schema} />
                      {renderRoleBadge?.(u.role)}
                      {u.superadmin && <Badge colorScheme="purple" variant="subtle">Superadmin</Badge>}
                    </HStack>
                    <HStack spacing={2} fontSize="xs" color="gray.500">
                      <Text>{formatLastActive(u.last_active)}</Text>
                      <Text>·</Text>
                      <Text overflowWrap="anywhere">
                        {u.schemas.length} schema{u.schemas.length === 1 ? "" : "s"}
                      </Text>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </Box>
      </Box>

      <Box flex="2" minW={0} borderWidth="1px" borderRadius="md" p={4}>
        {!selected ? (
          <Text fontSize="sm" color="gray.500">
            Search for a user and pick them to edit the schemas they can open.
          </Text>
        ) : (
          <VStack align="stretch" spacing={4}>
            <Box fontSize="sm">
              <HStack spacing={2} wrap="wrap">
                <Text fontWeight="semibold" fontSize="md">{selected.name || "(no name)"}</Text>
                {renderRoleBadge?.(selected.role)}
              </HStack>
              <Text color="gray.500" fontSize="xs">{selected.email}</Text>
              <Text mt={1} fontSize="xs" color="gray.600">
                Current: {current.join(", ") || "no schema"}
              </Text>
            </Box>

            {selected.is_self && (
              <Text fontSize="sm" color="orange.500">
                This is you. Refresh the page after saving for the change to take effect.
              </Text>
            )}

            <FormControl>
              <HStack spacing={3}>
                <Switch
                  id="superadmin-switch"
                  colorScheme="purple"
                  isChecked={selected.superadmin === true}
                  isDisabled={!!switchWhy || switching}
                  onChange={(e) => setSuperadmin(e.target.checked)}
                />
                <FormLabel htmlFor="superadmin-switch" fontSize="sm" mb={0}>Superadmin</FormLabel>
              </HStack>
              <Text fontSize="xs" color={switchWhy ? "orange.500" : "gray.500"} mt={1}>
                {switchWhy || SWITCH_WHY.about}
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Native schema</FormLabel>
              <Select
                placeholder="Pick an existing schema…"
                value={schemaOptions.includes(draft.nativeInput) ? draft.nativeInput : ""}
                onChange={(e) => setNative(e.target.value)}
                mb={2}
              >
                {schemaOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
              <Input
                size="sm"
                placeholder="…or type a schema name"
                value={draft.nativeInput}
                onChange={(e) => setNative(e.target.value)}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                The schema they land in at login. "{current[0] || "none"}" moves to the additional schemas.
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Additional schemas</FormLabel>
              <Wrap spacing={2} mb={2}>
                {draftSchemas.slice(1).map((s) => (
                  <WrapItem key={s}>
                    <Tag size="sm" colorScheme="blue" variant="subtle">
                      <TagLabel>{s}</TagLabel>
                      <TagCloseButton aria-label={`Remove ${s}`} onClick={() => removeExtra(s)} />
                    </Tag>
                  </WrapItem>
                ))}
                {draftSchemas.length <= 1 && (
                  <WrapItem>
                    <Text fontSize="xs" color="gray.400">none</Text>
                  </WrapItem>
                )}
              </Wrap>
              <HStack spacing={2}>
                <Select
                  size="sm"
                  placeholder="Add existing…"
                  value=""
                  onChange={(e) => e.target.value && addExtra(e.target.value)}
                >
                  {schemaOptions
                    .filter((s) => !draftSchemas.includes(s))
                    .map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                </Select>
                <Input
                  size="sm"
                  placeholder="…or type"
                  value={draft.extraInput}
                  onChange={(e) => setDraft((d) => ({ ...d, extraInput: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addExtra(draft.extraInput);
                    }
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  isDisabled={!draft.extraInput.trim()}
                  onClick={() => addExtra(draft.extraInput)}
                >
                  Add
                </Button>
              </HStack>
            </FormControl>

            <Text fontSize="xs" color="gray.600">
              Will save schemas:{" "}
              <Text as="span" fontWeight="semibold">{draftSchemas.join(", ") || "none"}</Text>. Their
              row is seeded in the native schema's users table and their role is copied into each
              schema.
            </Text>

            <HStack justify="flex-end" spacing={2}>
              {changed && !saving && (
                <Text fontSize="xs" color="orange.500">Unsaved changes — click Save schemas</Text>
              )}
              <Button size="sm" variant="ghost" isDisabled={!changed || saving} onClick={() => pick(selected)}>
                Reset
              </Button>
              <Button size="sm" colorScheme="blue" isDisabled={!canSave} isLoading={saving} onClick={save}>
                Save schemas
              </Button>
            </HStack>
          </VStack>
        )}
      </Box>
    </Flex>
  );
}
